<template>
  <div id="app" :class="{ 'night-mode': isNightMode }">
    <RouterView class="app-view" />
    
    <!-- Floating Controls (Mobile Only) -->
    <div class="global-controls" v-if="isMobileMode && $route.path === '/m/more'">
      <div class="control-btn" @click="togglePageMode" title="切换设备模式">
        PC版
      </div>
      <div class="control-btn" @click="toggleNightMode" title="切换夜间模式">
        {{ isNightMode ? '日间' : '夜间' }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  NIGHT_MODE_STORAGE_KEYS,
  activateManualNightMode,
  resolveNightModeEnabled
} from './utils/nightMode';

export default {
  name: 'App',
  data() {
    return {
      isNightMode: false,
      isMobileMode: false,
      nightModeTimer: null
    };
  },
  created() {
    this.initNightMode();
    this.initPageMode();
  },
  mounted() {
    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener('night-mode-change', this.handleNightModeEvent);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    if (this.nightModeTimer) clearInterval(this.nightModeTimer);
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('night-mode-change', this.handleNightModeEvent);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.syncNightModeDomClass(false);
  },
  methods: {
    syncNightModeDomClass(enabled) {
      if (typeof document === 'undefined') return;
      const targets = [document.body, document.documentElement];
      targets.forEach((target) => {
        if (!target || !target.classList) return;
        target.classList.toggle('night-mode', !!enabled);
      });
    },
    setNightMode(enabled, persist = false) {
      this.isNightMode = !!enabled;
      this.syncNightModeDomClass(this.isNightMode);
      if (persist) {
        localStorage.setItem('manualNightMode', String(this.isNightMode));
      }
    },
    emitNightModeEvent(enabled) {
      window.dispatchEvent(new CustomEvent('night-mode-change', {
        detail: { enabled: !!enabled, source: 'app' }
      }));
    },
    handleNightModeEvent(event) {
      const detail = event && event.detail ? event.detail : null;
      if (!detail || typeof detail.enabled !== 'boolean') return;
      this.setNightMode(detail.enabled, false);
    },
    handleStorageChange(event) {
      if (!event) return;
      if (event.key !== NIGHT_MODE_STORAGE_KEYS.manualEnabled && event.key !== NIGHT_MODE_STORAGE_KEYS.mode) return;
      this.checkNightMode();
    },
    initPageMode() {
      // Check current mode based on localStorage or URL
      const preferred = localStorage.getItem('preferredMode');
      const isMobileUrl = this.$route.path.startsWith('/m');
      
      if (preferred) {
        this.isMobileMode = preferred === 'mobile';
      } else {
        // Fallback to URL detection or device width
        this.isMobileMode = isMobileUrl;
      }
    },
    togglePageMode() {
      const newMode = !this.isMobileMode ? 'mobile' : 'pc';
      localStorage.setItem('preferredMode', newMode);
      this.isMobileMode = newMode === 'mobile';
      
      // Redirect
      if (newMode === 'mobile') {
        this.$router.replace('/m/daily-hot');
      } else {
        this.$router.replace('/');
      }
      
      // Force reload to ensure all guards and components reset properly if needed
      setTimeout(() => window.location.reload(), 100);
    },
    initNightMode() {
      this.checkNightMode();
      // Check every minute
      this.nightModeTimer = setInterval(() => {
        this.checkNightMode();
      }, 60000);
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.checkNightMode();
      }
    },
    checkNightMode() {
      const enabled = resolveNightModeEnabled(new Date());
      const changed = enabled !== this.isNightMode;
      this.setNightMode(enabled, false);
      if (changed) this.emitNightModeEvent(enabled);
    },
    toggleNightMode() {
      const next = !this.isNightMode;
      // 手动切换：作为临时覆盖，下个自动切换点(18:00/07:30)会自动恢复自动模式
      activateManualNightMode(next);
      this.setNightMode(next, false);
      this.emitNightModeEvent(next);
    }
  },
  watch: {
    $route(to) {
      this.isMobileMode = to.path.startsWith('/m');
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
}

/* Floating Controls */
.global-controls {
  position: fixed;
  top: 10px;
  right: 10px;
  z-index: 9999;
  display: flex;
  gap: 10px;
}
.control-btn {
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  transition: all 0.3s;
}
.control-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

/* Night Mode Styles */
.night-mode {
  background-color: #121212 !important;
  color: #e0e0e0 !important;
}

/* === Global Reset for Night Mode === */
.night-mode * {
  border-color: #333 !important;
}

/* Backgrounds */
.night-mode .mobile-page,
.night-mode .van-search,
.night-mode .van-tabbar,
.night-mode .van-pull-refresh,
.night-mode #app,
.night-mode .app-view {
  background-color: #121212 !important;
}

.night-mode .van-nav-bar,
.night-mode .van-cell,
.night-mode .van-cell-group,
.night-mode .van-popup,
.night-mode .van-dropdown-menu__bar,
.night-mode .van-dropdown-item__content,
.night-mode .van-action-sheet {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}

/* Action Bar and List Footers */
.night-mode .action-bar,
.night-mode .van-list__finished-text,
.night-mode .van-list__loading,
.night-mode .popup-footer,
.night-mode .detail-extra {
  background-color: #1e1e1e !important;
  color: #a0a0a0 !important;
}

.night-mode .delete-icon {
  background-color: #333 !important;
  color: #ee0a24 !important;
}

/* Card Styles - Unified */
.night-mode .ivu-card,
.night-mode .publish-card,
.night-mode .history-item,
.night-mode .cover-item,
.night-mode .account-item,
.night-mode .audit-card,
.night-mode .agent-card {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
  border: 1px solid #333 !important;
}

/* Remove white background from layout content area */
.night-mode .layout-content {
  background-color: #121212 !important; /* Match main background */
  padding: 10px !important;
  border-radius: 0 !important;
}

/* Ensure no child elements inject white backgrounds */
.night-mode .layout-base,
.night-mode .layout,
.night-mode .layout-main,
.night-mode .layout-base .layout-content > div {
  background-color: #121212 !important;
}

/* Force layout footer to be dark */
.night-mode .layout-footer {
  background-color: #121212 !important;
  color: #9ea7b4 !important;
}

/* Dark Scrollbar for Night Mode */
.night-mode ::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  background-color: #121212;
}
.night-mode ::-webkit-scrollbar-track {
  background-color: #121212;
  border-radius: 4px;
}
.night-mode ::-webkit-scrollbar-thumb {
  background-color: #333;
  border-radius: 4px;
}
.night-mode ::-webkit-scrollbar-thumb:hover {
  background-color: #444;
}
.night-mode ::-webkit-scrollbar-corner {
  background-color: #121212;
}

/* Inputs & Search */
.night-mode .van-search__content,
.night-mode .van-field__control {
  background-color: #2c2c2c !important;
  color: #e0e0e0 !important;
}

/* Bright Titles */
.night-mode .item-title,
.night-mode .publish-title,
.night-mode .cover-name,
.night-mode .agent-name,
.night-mode .audit-title,
.night-mode .van-cell__title,
.night-mode .van-nav-bar__title,
.night-mode .page-title,
.night-mode .title,
.night-mode .detail-title,
.night-mode .bright-value {
  color: #ffffff !important;
  font-weight: 600 !important;
}

/* Sub-text Brightness Bump */
.night-mode .van-cell__label,
.night-mode .van-tabbar-item,
.night-mode .van-search__action,
.night-mode .publish-meta,
.night-mode .info-row,
.night-mode .label,
.night-mode .detail-meta,
.night-mode .van-field__word-limit,
.night-mode .van-cell__value,
.night-mode .van-field__label,
.night-mode .van-dropdown-menu__title,
.night-mode .van-tabbar-item--active {
  color: #a0a0a0 !important;
}

/* Borders */
.night-mode .van-hairline--bottom::after,
.night-mode .van-hairline--top-bottom::after,
.night-mode .van-hairline--surround::after,
.night-mode .van-cell::after {
  border-color: #333 !important;
}

/* Buttons */
.night-mode .van-button,
.night-mode .van-button::before,
.night-mode .van-button::after {
  box-shadow: none !important;
  outline: none !important;
}
.night-mode .van-button::before,
.night-mode .van-button::after {
  border: 0 !important;
}
.night-mode .van-button {
  -webkit-tap-highlight-color: transparent;
}
.night-mode .van-button--info:not(.van-button--plain) {
  background-color: #00A88E !important;
  color: var(--ink) !important; /* 墨字：霓虹渐变图仍在 background-color 之上，白字压黄端不可读 */
  border-color: #00A88E !important;
}
.night-mode .van-button--primary:not(.van-button--plain) {
  background-color: #00A88E !important;
  color: var(--ink) !important; /* 墨字：霓虹渐变图仍在 background-color 之上，白字压黄端不可读 */
  border-color: #00A88E !important;
}
.night-mode .van-button--danger:not(.van-button--plain) {
  border-color: #ee0a24 !important;
}
.night-mode .van-button--warning:not(.van-button--plain) {
  border-color: #ff976a !important;
}
.night-mode .van-button--default {
  background-color: #333 !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}
.night-mode .van-button--primary.van-button--plain {
  background-color: transparent !important;
  color: var(--accent) !important;
  border-color: var(--accent) !important;
}
.night-mode .van-button--info.van-button--plain {
  background-color: transparent !important;
  color: var(--accent) !important;
  border-color: var(--accent) !important;
}
.night-mode .van-button--danger.van-button--plain {
  background-color: transparent !important;
  color: #ee0a24 !important;
  border-color: #ee0a24 !important;
}
.night-mode .van-button--warning.van-button--plain {
  background-color: transparent !important;
  color: #ff976a !important;
  border-color: #ff976a !important;
}
.night-mode .van-button--success:not(.van-button--plain) {
  border-color: #07c160 !important;
}
.night-mode .van-button--success.van-button--plain {
  background-color: transparent !important;
  color: #07c160 !important;
  border-color: #07c160 !important;
}

/* Specific Component Tweaks */
.night-mode .van-picker {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}
.night-mode .van-picker__mask {
  background-image: linear-gradient(180deg, rgba(30,30,30,0.9), rgba(30,30,30,0.4)), linear-gradient(0deg, rgba(30,30,30,0.9), rgba(30,30,30,0.4)) !important;
}
.night-mode .van-picker-column__item {
  color: #aaa !important;
}
.night-mode .van-picker-column__item--selected {
  color: #fff !important;
}

/* Dialogs & Popups Extras */
.night-mode .van-dialog,
.night-mode .van-dialog__header,
.night-mode .van-dialog__content,
.night-mode .van-dialog__footer {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}
.night-mode .van-dialog__message {
  color: #ccc !important;
}
.night-mode .van-grid-item__content {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}
.night-mode .van-radio__label,
.night-mode .van-checkbox__label {
  color: #e0e0e0 !important;
}
.night-mode .van-cell-group__title {
  color: #a0a0a0 !important;
}

/* Error/Warning Boxes */
.night-mode .error-msg,
.night-mode .error-box {
  background-color: #2a1212 !important;
  color: #ff6b6b !important;
  border-color: #442222 !important;
}
.night-mode .error-title {
  color: #ff4d4f !important;
}
.night-mode .error-content {
  color: #ff6b6b !important;
}

/* Custom Sections */
.night-mode .detail-section {
  background-color: #252525 !important;
}
.night-mode .user-profile {
  background: linear-gradient(to right, #0d253f, #104a8e) !important;
}

/* === PC (iView / View Design) === */

/* Left Sider Menu */
.night-mode .app-sider,
.night-mode .layout-col-left,
.night-mode .app-sider .ivu-menu-dark,
.night-mode .layout-col-left .ivu-menu-dark {
  background-color: #1e1e1e !important;
}

.night-mode .sideHeader {
  background-color: #1e1e1e !important;
  border-bottom: 1px solid #333;
}

/* Menu Item Default State */
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item,
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-submenu-title,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-submenu-title {
  color: #a0a0a0 !important;
  background-color: transparent !important;
  transition: all 0.3s;
}

/* Hover State */
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item:hover,
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-submenu-title:hover,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item:hover,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-submenu-title:hover {
  color: #fff !important;
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Active/Selected State */
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item-active:not(.ivu-menu-submenu),
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item-active:not(.ivu-menu-submenu):hover,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item-active:not(.ivu-menu-submenu),
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-item-active:not(.ivu-menu-submenu):hover {
  background-color: #00A88E !important; /* Brand Blue */
  color: #fff !important;
  border-right: none !important;
}

/* Submenu Open Background */
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-opened .ivu-menu-submenu-title,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-opened .ivu-menu-submenu-title {
  background-color: #252525 !important;
}
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-opened,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-opened {
  background-color: #181818 !important; /* Slightly darker for expanded area */
}

/* Active Submenu Parent */
.night-mode .app-sider .ivu-menu-dark.ivu-menu-vertical .ivu-menu-child-item-active > .ivu-menu-submenu-title,
.night-mode .layout-col-left .ivu-menu-dark.ivu-menu-vertical .ivu-menu-child-item-active > .ivu-menu-submenu-title {
  color: #fff !important;
}

.night-mode .sideHeader p {
  color: #e0e0e0 !important;
}

.night-mode .header-con,
.night-mode .main-header-con,
.night-mode .tag-nav-wrapper,
.night-mode .layout-header-bar,
.night-mode .layout-header,
.night-mode .app-view .ivu-card,
.night-mode .main {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
  box-shadow: none !important;
}

/* Menu */
.night-mode .layout-base .ivu-menu-light,
.night-mode .layout .ivu-menu-light {
  background: #1e1e1e !important;
}
.night-mode .layout-base .ivu-menu-light.ivu-menu-vertical .ivu-menu-item-active:not(.ivu-menu-submenu),
.night-mode .layout .ivu-menu-light.ivu-menu-vertical .ivu-menu-item-active:not(.ivu-menu-submenu) {
  background: #00A88E !important;
  color: #fff !important;
}
.night-mode .layout-base .ivu-menu-item,
.night-mode .layout-base .ivu-menu-submenu-title,
.night-mode .layout .ivu-menu-item,
.night-mode .layout .ivu-menu-submenu-title {
  color: #aaa !important;
}
.night-mode .layout-base .ivu-menu-item:hover,
.night-mode .layout-base .ivu-menu-submenu-title:hover,
.night-mode .layout .ivu-menu-item:hover,
.night-mode .layout .ivu-menu-submenu-title:hover {
  color: #fff !important;
}

/* Tables */
.night-mode .ivu-table,
.night-mode .ivu-table th,
.night-mode .ivu-table td {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
  border-bottom-color: #333 !important;
}
.night-mode .ivu-table-wrapper {
  border-color: #333 !important;
}
.night-mode .ivu-table-stripe .ivu-table-body tr:nth-child(2n) td,
.night-mode .ivu-table-stripe .ivu-table-fixed-body tr:nth-child(2n) td {
  background-color: #252525 !important;
}
.night-mode .ivu-table-row-hover td {
  background-color: var(--accent-soft) !important;
}

/* Inputs & Forms */
.night-mode .ivu-input,
.night-mode .ivu-input-number,
.night-mode .ivu-input-number-input,
.night-mode .ivu-input-number-handler-wrap,
.night-mode .ivu-input-number-handler,
.night-mode .ivu-select-selection,
.night-mode .ivu-select-dropdown,
.night-mode .ivu-dropdown-menu {
  background-color: #2c2c2c !important;
  border-color: #444 !important;
  color: #e0e0e0 !important;
}
.night-mode .ivu-input-number:hover,
.night-mode .ivu-input-number-focused {
  border-color: #444 !important;
}
.night-mode .ivu-input-number-handler:hover {
  background-color: #3a3a3a !important;
}
.night-mode .ivu-input-number-handler-up-inner,
.night-mode .ivu-input-number-handler-down-inner {
  color: #aeb6c2 !important;
}
.night-mode .ivu-dropdown-item {
  color: #e0e0e0 !important;
}
.night-mode .ivu-dropdown-item:hover {
  background-color: #444 !important;
  color: #fff !important;
}
.night-mode .ivu-select-item {
  color: #e0e0e0 !important;
}
.night-mode .ivu-select-item:hover,
.night-mode .ivu-select-item-focus {
  background-color: #444 !important;
}
/* Select 选中值/可输入文本：view-ui-plus 默认色 #17233d，夜间在深底上不可读；占位符见下方统一规则 */
.night-mode .ivu-select-selected-value,
.night-mode .ivu-select-input {
  color: #e0e0e0 !important;
}
/* 占位符统一色阶：页面级覆盖（如 --text-meta #6E7590）是配 token 深蓝底设计的，
   而夜间块上面已用 !important 把输入框/选择框底色统一为 #2c2c2c——占位符若不在此定色，
   页面色会落在错配的底上显得又暗又突兀（提示词模板筛选区等）。与上方 meta 文案色阶一致 */
.night-mode .ivu-input::placeholder,
.night-mode .ivu-input-number-input::placeholder,
.night-mode .ivu-select-placeholder {
  color: #a0a0a0 !important;
}

/* Collapse 折叠面板（MCP 问题排查等）：simple 模式下头/内容沿用浅色主题的 #666/#515a6e，夜间不可读 */
.night-mode .ivu-collapse {
  background-color: transparent !important;
  border-color: #333 !important;
}
.night-mode .ivu-collapse > .ivu-collapse-item > .ivu-collapse-header {
  color: #e0e0e0 !important;
}
.night-mode .ivu-collapse-content,
.night-mode .ivu-collapse-content > .ivu-collapse-content-box {
  background-color: transparent !important;
  color: #d5d9e0 !important;
}

/* Alert 提示条：夜间换深色表面。浅色主题的浅底与全局 reset(.night-mode div/span color:inherit)
   叠加会导致「浅底浅字」不可读（如 Skill 管理顶部说明、MCP 接入说明顶部），与 apiSetting 页先例同款 */
.night-mode .ivu-alert {
  background-color: #252525 !important;
  border-color: #3a3a3a !important;
}
.night-mode .ivu-alert .ivu-alert-message,
.night-mode .ivu-alert .ivu-alert-desc {
  color: #d5d9e0 !important;
}

/* Modals */
.night-mode .ivu-modal-content,
.night-mode .ivu-modal-header,
.night-mode .ivu-modal-footer {
  background-color: #1e1e1e !important;
  border-color: #333 !important;
}
.night-mode .ivu-modal-header-inner,
.night-mode .ivu-modal-close .ivu-icon-ios-close {
  color: #e0e0e0 !important;
}

/* Drawers */
.night-mode .ivu-drawer-content,
.night-mode .ivu-drawer-header,
.night-mode .ivu-drawer-body {
  background-color: #1e1e1e !important;
  border-color: #333 !important;
}
.night-mode .ivu-drawer-header-inner,
.night-mode .ivu-drawer-close .ivu-icon-ios-close {
  color: #e0e0e0 !important;
}

/* 图文内容编辑抽屉(TextContentEditDrawer)夜间适配 */
.night-mode .text-content-edit-drawer .ivu-form-item-label,
.night-mode .text-content-edit-drawer .ivu-radio-wrapper,
.night-mode .text-content-edit-drawer .ivu-checkbox-wrapper,
.night-mode .text-content-edit-drawer .ivu-divider-inner-text,
.night-mode .text-content-edit-drawer .tced-col-title,
.night-mode .text-content-edit-drawer details,
.night-mode .text-content-edit-drawer summary,
.night-mode .text-content-edit-drawer .tced-original-preview,
.night-mode .text-content-edit-drawer .tced-footer {
  color: #e0e0e0 !important;
}
.night-mode .text-content-edit-drawer .tced-footer {
  background-color: #1e1e1e !important;
  border-top-color: #333 !important;
}
.night-mode .text-content-edit-drawer .tced-original-preview {
  border-color: #444 !important;
}
.night-mode .text-content-edit-drawer details summary {
  color: #aeb6c2 !important;
}

/* System Message Drawer */
.night-mode .message-item {
  background-color: #242424 !important;
}
.night-mode .message-divider {
  background-color: #333 !important;
}
.night-mode .message-menu {
  background-color: #242424 !important;
  border-color: #444 !important;
  color: #ccc !important;
}
.night-mode .message-title {
  color: #e0e0e0 !important;
}
.night-mode .message-content {
  color: #999 !important;
}
.night-mode .message-time {
  color: #666 !important;
}

/* Breadcrumb & Tabs */
.night-mode .ivu-breadcrumb-item-separator,
.night-mode .ivu-breadcrumb > span:last-child {
  color: #888 !important;
}
.night-mode .ivu-tag {
  background: #333 !important;
  border-color: #444 !important;
}
.night-mode .ivu-tag .ivu-tag-text {
  color: #ccc !important;
}

/* WangEditor */
.night-mode {
  --w-e-textarea-bg-color: #1e1e1e !important;
  --w-e-textarea-color: #e0e0e0 !important;
  --w-e-textarea-border-color: #333 !important;
  --w-e-textarea-slight-border-color: #444 !important;
  --w-e-textarea-slight-color: #888 !important;
  --w-e-textarea-slight-bg-color: #2b2b2b !important;
  --w-e-textarea-selected-border-color: #555 !important;
  --w-e-textarea-selected-bg-color: rgba(255, 255, 255, 0.1) !important;
  --w-e-textarea-handler-bg-color: #555 !important;

  --w-e-toolbar-color: #aaa !important;
  --w-e-toolbar-bg-color: #1e1e1e !important;
  --w-e-toolbar-active-color: #fff !important;
  --w-e-toolbar-active-bg-color: #333 !important;
  --w-e-toolbar-disabled-color: #666 !important;
  --w-e-toolbar-border-color: #333 !important;

  --w-e-modal-button-bg-color: #2b2b2b !important;
  --w-e-modal-button-border-color: #444 !important;
}

.night-mode .editor-wrapper,
.night-mode [style*="border: 1px solid #ccc"],
.night-mode [style*="border: 1px solid rgb(204, 204, 204)"] {
  border-color: #333 !important;
}

.night-mode .editor-wrapper > div:first-child,
.night-mode [style*="border-bottom: 1px solid #ccc"],
.night-mode [style*="border-bottom: 1px solid rgb(204, 204, 204)"] {
  border-bottom-color: #333 !important;
}

.night-mode [style*="border-left: 1px solid #f0f0f0"] {
  border-left-color: #333 !important;
}

/* Common Text */
.night-mode h1, .night-mode h2, .night-mode h3, .night-mode h4, .night-mode h5, .night-mode h6,
.night-mode p, .night-mode span, .night-mode div {
  color: inherit;
}
.night-mode .title,
.night-mode .detail-title {
  color: #fff !important;
}

/* ===== Neon Chrome 深色增益（spec §3.5）===== */
.night-mode .ivu-btn-primary:hover {
  box-shadow: var(--glow-md);
}
.night-mode .ivu-table-header th {
  background: var(--table-head-bg);
}
.night-mode .van-tabbar-item--active .van-tabbar-item__icon {
  box-shadow: var(--glow-md);
}
.night-mode .van-nav-bar {
  background: var(--card-bg);
}
</style>
